import { useParams, Link, Navigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { SEO } from '@/components/SEO';
import { blogPosts, type BlogPost } from '@/data/blogPosts';
import { Calendar, Clock, ArrowLeft, ArrowRight, Tag, Loader2, Share2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, limit } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { getSafeBlogImageUrl } from '@/lib/utils';

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPost | null>(() => {
    return blogPosts.find(p => p.slug === slug) || null;
  });
  const [allPosts, setAllPosts] = useState<BlogPost[]>(blogPosts);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPostAndRelated = async () => {
      if (!db || !slug) return;
      try {
        const q = query(collection(db, 'blogPosts'), where('slug', '==', slug), limit(1));
        const snapshot = await getDocs(q);
        
        if (!snapshot.empty) {
          const docData = snapshot.docs[0].data();
          const currentPost = {
            ...docData,
            tags: docData.tags || [],
          } as BlogPost;
          setPost(currentPost);
        }

        const allSnapshot = await getDocs(collection(db, 'blogPosts'));
        if (!allSnapshot.empty) {
          const list = allSnapshot.docs.map(doc => {
            const data = doc.data();
            return {
              ...data,
              tags: data.tags || [],
            } as BlogPost;
          });
          setAllPosts(list);
        }
      } catch (error) {
        console.error('Error fetching blog post:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPostAndRelated();
  }, [slug]);

  if (loading && !post) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading article...</p>
        </div>
      </div>
    );
  }

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  // Find related posts
  const relatedPosts = allPosts
    .filter(p => p.id !== post.id && (p.category === post.category || p.tags.some(tag => post.tags.includes(tag))))
    .slice(0, 3);

  const handleShare = async () => {
    const url = window.location.href;
    const title = post.title;
    
    if (navigator.share) {
      try {
        await navigator.share({ title, url });
      } catch (error) {
        console.log('Share cancelled');
      }
    } else {
      // Fallback: copy to clipboard
      await navigator.clipboard.writeText(url);
      alert('Link copied to clipboard!');
    }
  };

  // Generate structured data for the article
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.excerpt,
    author: {
      '@type': 'Organization',
      name: post.author,
    },
    datePublished: post.date,
    dateModified: post.date,
    publisher: {
      '@type': 'Organization',
      name: 'QuickFreeInvoice',
      logo: {
        '@type': 'ImageObject',
        url: 'https://quickfreeinvoicetool.com/logo.png',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://quickfreeinvoicetool.com/blog/${post.slug}`,
    },
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title={`${post.title} | Free Invoice Guide — QuickFreeInvoice`}
        description={post.excerpt}
        keywords={post.tags.join(', ')}
      />
      
      {/* Inject Article Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />

      {/* Header */}
      <section className="bg-white border-b">
        <div className="container mx-auto px-4 py-8">
          <Link to="/blog">
            <Button variant="ghost" className="mb-6">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Blog
            </Button>
          </Link>
          
          <div className="max-w-4xl mx-auto">
            <Badge className="mb-4">{post.category}</Badge>
            <h1 className="font-heading text-3xl md:text-5xl font-bold mb-6">
              {post.title}
            </h1>
            
            <div className="flex flex-wrap items-center gap-6 text-muted-foreground mb-6">
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                <span>
                  {new Date(post.date).toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                <span>{post.readTime}</span>
              </div>
              <div className="flex items-center gap-2">
                <span>By {post.author}</span>
              </div>
              {/* Social Share Buttons */}
              <div className="flex items-center gap-2 ml-auto flex-wrap">
                <span className="text-xs text-muted-foreground font-medium hidden sm:inline">Share:</span>
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(typeof window !== 'undefined' ? window.location.href : 'https://quickfreeinvoicetool.com/blog/' + post.slug)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Share post on Facebook"
                  className="px-2.5 py-1 text-xs font-medium bg-slate-100 hover:bg-blue-700 hover:text-white rounded transition-colors text-slate-700 flex items-center gap-1"
                >
                  <svg className="w-3 h-3 fill-current" viewBox="0 0 24 24">
                    <path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H7.5v-3H10V9.5C10 7.01 11.49 5.6 13.73 5.6c1.07 0 2.19.19 2.19.19v2.41h-1.24c-1.23 0-1.61.77-1.61 1.56V12h2.72l-.43 3H13v6.8c4.56-.93 8-4.96 8-9.8z" />
                  </svg>
                  Facebook
                </a>
                <a
                  href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(typeof window !== 'undefined' ? window.location.href : 'https://quickfreeinvoicetool.com/blog/' + post.slug)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Share post on Twitter / X"
                  className="px-2.5 py-1 text-xs font-medium bg-slate-100 hover:bg-indigo-600 hover:text-white rounded transition-colors text-slate-700 flex items-center gap-1"
                >
                  <svg className="w-3 h-3 fill-current" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                  X
                </a>
                <a
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(typeof window !== 'undefined' ? window.location.href : 'https://quickfreeinvoicetool.com/blog/' + post.slug)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Share post on LinkedIn"
                  className="px-2.5 py-1 text-xs font-medium bg-slate-100 hover:bg-blue-600 hover:text-white rounded transition-colors text-slate-700 flex items-center gap-1"
                >
                  LinkedIn
                </a>
                <a
                  href={`https://api.whatsapp.com/send?text=${encodeURIComponent(post.title + ' ' + (typeof window !== 'undefined' ? window.location.href : 'https://quickfreeinvoicetool.com/blog/' + post.slug))}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Share post on WhatsApp"
                  className="px-2.5 py-1 text-xs font-medium bg-slate-100 hover:bg-emerald-600 hover:text-white rounded transition-colors text-slate-700 flex items-center gap-1"
                >
                  WhatsApp
                </a>
                <button
                  onClick={handleShare}
                  aria-label="More share options or copy link"
                  className="px-2.5 py-1 text-xs font-medium bg-slate-100 hover:bg-slate-800 hover:text-white rounded transition-colors text-slate-700 flex items-center gap-1"
                >
                  <Share2 className="w-3 h-3" />
                  More
                </button>
              </div>
            </div>

            {/* Tags */}
            <div className="flex items-center gap-2 flex-wrap">
              <Tag className="h-4 w-4 text-muted-foreground" />
              {post.tags.map(tag => (
                <Badge key={tag} variant="secondary" className="text-xs">
                  {tag}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="overflow-hidden">
              {post.image && (
                <div className="w-full h-[250px] md:h-[400px] overflow-hidden border-b">
                  <img 
                    src={getSafeBlogImageUrl(post.image)} 
                    alt={post.title} 
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <CardContent className="p-8 md:p-12">
                <div className="prose prose-lg max-w-none">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      h1: ({ node, ...props }) => <h1 className="text-3xl font-bold mb-4 mt-8" {...props} />,
                      h2: ({ node, ...props }) => <h2 className="text-2xl font-bold mb-3 mt-6" {...props} />,
                      h3: ({ node, ...props }) => <h3 className="text-xl font-semibold mb-2 mt-4" {...props} />,
                      p: ({ node, ...props }) => <p className="mb-4 leading-relaxed text-gray-700" {...props} />,
                      ul: ({ node, ...props }) => <ul className="list-disc pl-6 mb-4 space-y-2" {...props} />,
                      ol: ({ node, ...props }) => <ol className="list-decimal pl-6 mb-4 space-y-2" {...props} />,
                      li: ({ node, ...props }) => <li className="text-gray-700" {...props} />,
                      strong: ({ node, ...props }) => <strong className="font-semibold text-gray-900" {...props} />,
                      code: ({ node, ...props }) => <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono" {...props} />,
                      pre: ({ node, ...props }) => <pre className="bg-gray-100 p-4 rounded-lg overflow-x-auto mb-4" {...props} />,
                      blockquote: ({ node, ...props }) => <blockquote className="border-l-4 border-primary pl-4 italic my-4 text-gray-600" {...props} />,
                      table: ({ node, ...props }) => (
                        <div className="overflow-x-auto my-6 border border-gray-200 rounded-lg shadow-sm">
                          <table className="min-w-full divide-y divide-gray-200 text-sm" {...props} />
                        </div>
                      ),
                      thead: ({ node, ...props }) => <thead className="bg-gray-50 font-semibold text-gray-900 border-b border-gray-200" {...props} />,
                      tbody: ({ node, ...props }) => <tbody className="divide-y divide-gray-200 bg-white" {...props} />,
                      tr: ({ node, ...props }) => <tr className="hover:bg-gray-50/50 transition-colors" {...props} />,
                      th: ({ node, ...props }) => <th className="px-4 py-3 text-left font-semibold text-gray-900" {...props} />,
                      td: ({ node, ...props }) => <td className="px-4 py-3 text-gray-700 align-middle" {...props} />,
                    }}
                  >
                    {post.content}
                  </ReactMarkdown>
                </div>
              </CardContent>
            </Card>

            {/* CTA Section */}
            <Card className="mt-8 bg-gradient-to-r from-primary to-secondary text-white">
              <CardContent className="p-8 text-center">
                <h3 className="text-2xl font-bold mb-3">
                  Ready to Create Professional Invoices?
                </h3>
                <p className="text-indigo-100 mb-6">
                  Put these tips into action. Start creating invoices in minutes with QuickFreeInvoice.
                </p>
                <Link to="/register">
                  <Button size="lg" className="bg-white text-primary hover:bg-gray-100">
                    Get Started Free
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-12 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="font-heading text-3xl font-bold mb-8 text-center">
                Related Articles
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedPosts.map(relatedPost => (
                  <Card key={relatedPost.id} className="hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <Badge className="mb-3">{relatedPost.category}</Badge>
                      <h3 className="font-semibold text-lg mb-2 line-clamp-2">
                        <Link 
                          to={`/blog/${relatedPost.slug}`}
                          className="hover:text-primary transition-colors"
                        >
                          {relatedPost.title}
                        </Link>
                      </h3>
                      <p className="text-sm text-muted-foreground mb-4 line-clamp-3">
                        {relatedPost.excerpt}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        <span>{relatedPost.readTime}</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
